import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, MonitorPlay, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { AlertCountBadge, AvBadge, BillingBadge, DeviceStatusBadge, PatchBadge } from '@/components/rmm/DeviceBadges';
import { useRmmBilling, useRmmDevices, type RmmDevice, type RmmDeviceBilling, type RmmSite } from '@/api/rmm';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { daysSince } from '@/lib/rmm-helpers';
import { ROUTES, rmmClientSitesPath, rmmDevicePath, rmmSitePath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { OrbitLink } from '@/components/insights/OrbitLink';

/**
 * Sites — the site list is grouped by client (Autotask company). Pick a site to
 * see its devices, or pick a multi-site client to see every device across its
 * sites. The selection lives in the URL (/rmm/sites/:uid or
 * /rmm/sites/client/:companyId) so it can be linked to.
 *
 * Everything comes from the same cached /api/rmm/devices payload as the Devices
 * grid (it carries the site list), so this page costs Datto nothing extra.
 */

interface SiteRow extends RmmSite {
  devices: RmmDevice[];
}

/**
 * One client and its Datto sites. Sites not linked to an Autotask company can't
 * be grouped, so each becomes its own group named after the site.
 */
interface ClientGroup {
  key: string;
  companyId: number | null;
  name: string;
  sites: SiteRow[];
  devices: RmmDevice[];
}

const HIDE_EMPTY_KEY = 'cosmos:rmm:sites:hideEmpty';
const EXPANDED_KEY = 'cosmos:rmm:sites:expanded';

function loadSet(key: string): Set<string> {
  try { const r = localStorage.getItem(key); return new Set(r ? JSON.parse(r) as string[] : []); } catch { return new Set(); }
}

export default function RmmSitesPage() {
  const { uid, companyId: companyParam } = useParams();
  const selectedCompanyId = companyParam != null ? Number(companyParam) : null;
  const navigate = useNavigate();
  const devices = useRmmDevices({ scoped: true });
  const [query, setQuery] = useState('');
  const [hideEmpty, setHideEmpty] = useState(() => {
    try { return localStorage.getItem(HIDE_EMPTY_KEY) !== '0'; } catch { return true; }
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => loadSet(EXPANDED_KEY));
  useEffect(() => { try { localStorage.setItem(HIDE_EMPTY_KEY, hideEmpty ? '1' : '0'); } catch { /* storage unavailable */ } }, [hideEmpty]);
  useEffect(() => { try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded])); } catch { /* storage unavailable */ } }, [expanded]);

  const groups = useMemo<ClientGroup[]>(() => {
    const bySite = new Map<string, RmmDevice[]>();
    for (const d of devices.data?.items ?? []) {
      const list = bySite.get(d.siteUid) ?? [];
      list.push(d);
      bySite.set(d.siteUid, list);
    }
    const byClient = new Map<string, ClientGroup>();
    for (const s of devices.data?.sites ?? []) {
      const list = bySite.get(s.uid) ?? [];
      const site: SiteRow = { ...s, devices: list };
      const key = s.autotaskCompanyId != null ? `c:${s.autotaskCompanyId}` : `s:${s.uid}`;
      const g = byClient.get(key) ?? {
        key, companyId: s.autotaskCompanyId, name: s.autotaskCompanyName ?? s.name, sites: [], devices: [],
      };
      g.sites.push(site);
      g.devices.push(...list);
      byClient.set(key, g);
    }
    for (const g of byClient.values()) g.sites.sort((a, b) => a.name.localeCompare(b.name));
    return [...byClient.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [devices.data]);

  // Resolve the selection: a single site, or a whole client.
  const selectedGroup = selectedCompanyId != null
    ? groups.find((g) => g.companyId === selectedCompanyId)
    : uid ? groups.find((g) => g.sites.some((s) => s.uid === uid)) : undefined;
  const selectedSite = uid ? selectedGroup?.sites.find((s) => s.uid === uid) : undefined;

  // Always show the group holding the selection opened, so the selected site is visible.
  useEffect(() => {
    if (selectedGroup && selectedGroup.sites.length > 1 && !expanded.has(selectedGroup.key)) {
      setExpanded((prev) => new Set(prev).add(selectedGroup.key));
    }
  }, [selectedGroup?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const shown = groups
    .map((g) => {
      const clientMatch = !q || g.name.toLowerCase().includes(q);
      const sites = g.sites.filter((s) =>
        (!hideEmpty || s.devices.length > 0 || s.uid === uid) &&
        (clientMatch || s.name.toLowerCase().includes(q)),
      );
      return { ...g, sites };
    })
    .filter((g) => g.sites.length > 0);
  const siteCount = shown.reduce((n, g) => n + g.sites.length, 0);

  function toggle(key: string) {
    setExpanded((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  }

  function selectGroup(g: ClientGroup) {
    // A one-site client IS its site; a multi-site client opens the combined view.
    if (g.sites.length === 1 || g.companyId == null) navigate(rmmSitePath(g.sites[0].uid));
    else navigate(rmmClientSitesPath(g.companyId));
  }

  const notFound = devices.data && (uid || selectedCompanyId != null) && !selectedGroup;

  return (
    <DesktopOnly
      title="Sites are available on desktop"
      message="Open Cosmos on a larger screen to browse RMM sites. The mobile app focuses on tickets and opportunities."
    >
      <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
        <PageHeader
          title="Sites"
          count={devices.data ? siteCount : null}
          isFetching={devices.isFetching}
          subtitle={
            <>
              Datto RMM · {devices.data ? `${shown.length} clients · ` : ''}Select a client or site to see its devices
              {devices.data && <> · Updated {fmtRelative(devices.data.fetchedAt)}</>}
            </>
          }
        />

        {devices.error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load sites: {devices.error instanceof Error ? devices.error.message : 'Unknown error'}
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-[360px_1fr] items-start gap-4">
          {/* ── Client / site list ── */}
          <aside className="sticky top-4 flex max-h-[calc(100vh-7rem)] flex-col rounded-lg border border-border bg-card shadow-sm">
            <div className="space-y-2 border-b border-divider p-3">
              <SearchInput withIcon placeholder="Search client or site" className="w-full" value={query} onValueChange={setQuery} />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} className="h-3.5 w-3.5" />
                Hide sites with no devices
              </label>
            </div>
            <div className="overflow-y-auto">
              {devices.isLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading sites…</div>
              ) : shown.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No clients or sites match.</div>
              ) : (
                <ul className="divide-y divide-divider">
                  {shown.map((g) => {
                    const multi = g.sites.length > 1;
                    // Searching opens every matching group so matched sites are visible.
                    const open = multi && (expanded.has(g.key) || !!q);
                    const groupSelected = multi
                      ? selectedCompanyId != null && g.companyId === selectedCompanyId
                      : g.sites[0].uid === uid;
                    return (
                      <li key={g.key}>
                        <div className={cn('flex items-stretch', groupSelected ? 'bg-muted' : 'hover:bg-muted/40')}>
                          {multi ? (
                            <button type="button" onClick={() => toggle(g.key)} aria-expanded={open}
                              title={open ? 'Collapse sites' : 'Show sites'}
                              className="flex w-7 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground">
                              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          ) : <span className="w-7 shrink-0" />}
                          <button type="button" onClick={() => selectGroup(g)} aria-current={groupSelected ? 'true' : undefined}
                            className="flex min-w-0 flex-1 items-center justify-between gap-3 py-2 pr-3 text-left">
                            <span className={cn('truncate text-sm text-foreground', groupSelected ? 'font-semibold' : 'font-medium')}>{g.name}</span>
                            <DeviceCount n={g.devices.length} />
                          </button>
                        </div>
                        {open && (
                          <ul className="border-t border-divider bg-background/30">
                            {g.sites.map((s) => (
                              <li key={s.uid}>
                                <button type="button" onClick={() => navigate(rmmSitePath(s.uid))} aria-current={s.uid === uid ? 'true' : undefined}
                                  className={cn('flex w-full items-center justify-between gap-3 py-1.5 pl-10 pr-3 text-left transition-colors',
                                    s.uid === uid ? 'bg-muted' : 'hover:bg-muted/40')}>
                                  <span className={cn('truncate text-sm', s.uid === uid ? 'font-semibold text-foreground' : 'text-foreground/90')}>{s.name}</span>
                                  <DeviceCount n={s.devices.length} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* ── Selection ── */}
          {selectedSite && selectedGroup ? (
            <DevicePanel
              key={selectedSite.uid}
              title={selectedGroup.name}
              subtitle={<>Datto site <span className="font-mono text-xs">{selectedSite.name}</span>
                {selectedGroup.sites.length > 1 && selectedGroup.companyId != null && (
                  <> · <Link to={rmmClientSitesPath(selectedGroup.companyId)} className="hover:underline">all {selectedGroup.sites.length} sites</Link></>
                )}
                {selectedGroup.companyId != null && <> · <OrbitLink companyId={selectedGroup.companyId}>Orbit</OrbitLink></>}</>}
              description={selectedSite.description}
              portalUrl={selectedSite.portalUrl}
              devices={selectedSite.devices}
            />
          ) : selectedGroup ? (
            <DevicePanel
              key={selectedGroup.key}
              title={selectedGroup.name}
              subtitle={<>All devices across {selectedGroup.sites.length} Datto sites: <span className="font-mono text-xs">{selectedGroup.sites.map((s) => s.name).join(', ')}</span>
                {selectedGroup.companyId != null && <> · <OrbitLink companyId={selectedGroup.companyId}>Orbit</OrbitLink></>}</>}
              devices={selectedGroup.devices}
              showSite
            />
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center">
              <MousePointerClick className="h-8 w-8 text-muted-foreground" />
              <div className="text-sm font-medium text-foreground">{notFound ? 'Not found' : 'Select a client or site'}</div>
              <p className="max-w-xs text-xs text-muted-foreground">
                {notFound ? 'That site or client no longer exists in Datto RMM.' : 'Pick from the list to see its devices.'}
              </p>
              {notFound && (
                <Button asChild variant="outline" size="sm" className="mt-1"><Link to={ROUTES.rmm.sites}>Back to all sites</Link></Button>
              )}
            </div>
          )}
        </div>
      </div>
    </DesktopOnly>
  );
}

function DeviceCount({ n }: { n: number }) {
  return <span className="shrink-0 text-sm tabular-nums text-muted-foreground" title={`${n} device${n === 1 ? '' : 's'}`}>{n}</span>;
}

// ---- Device panel (one site, or a whole client) ----

function DevicePanel({
  title, subtitle, description, portalUrl, devices, showSite = false,
}: {
  title: string;
  subtitle: React.ReactNode;
  description?: string | null;
  portalUrl?: string;
  devices: RmmDevice[];
  /** Combined client view — devices come from several sites. */
  showSite?: boolean;
}) {
  const [q, setQ] = useState('');
  const [notBilledOnly, setNotBilledOnly] = useState(false);
  const billing = useRmmBilling();
  const billOf = (d: RmmDevice): RmmDeviceBilling | undefined => billing.data?.items[d.uid];
  // Matches the gap list: an agent (not an ESXi host / printer) that isn't billed and isn't Synfo's own.
  const notBilled = (d: RmmDevice) => {
    const b = billOf(d);
    return d.deviceClass === 'device' && !!b && b.state !== 'billed' && b.state !== 'internal';
  };

  const stats = useMemo(() => ({
    online: devices.filter((d) => d.online).length,
    stale: devices.filter((d) => !d.online && (daysSince(d.lastSeen) ?? Infinity) >= 30).length,
    alerts: devices.filter((d) => d.openAlerts > 0).length,
    reboot: devices.filter((d) => d.rebootRequired).length,
    patchErr: devices.filter((d) => d.patchStatus === 'InstallError').length,
    notBilled: devices.filter(notBilled).length,
  }), [devices, billing.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = q.trim().toLowerCase();
  const shown = useMemo(() => devices
    .filter((d) => !notBilledOnly || notBilled(d))
    .filter((d) => !s || [d.hostname, d.description, d.lastLoggedInUser, d.intIpAddress, d.category, d.siteName, d.hardware?.serialNumber]
      .some((v) => v?.toLowerCase().includes(s)))
    // Online first, then hostname — the devices you can act on now are on top.
    .sort((a, b) => Number(b.online) - Number(a.online) || a.hostname.localeCompare(b.hostname)),
  [devices, s, notBilledOnly, billing.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const tiles: { label: string; value: number; tone?: 'warning' | 'destructive' }[] = [
    { label: 'Devices', value: devices.length },
    { label: 'Online', value: stats.online },
    // Short labels: seven tiles share the detail panel's width.
    { label: 'Offline 30d+', value: stats.stale, tone: 'warning' },
    { label: 'Open alerts', value: stats.alerts, tone: 'warning' },
    { label: 'Needs reboot', value: stats.reboot, tone: 'warning' },
    { label: 'Patch errors', value: stats.patchErr, tone: 'destructive' },
    { label: 'Not billed', value: stats.notBilled, tone: 'warning' },
  ];

  const headers = ['Hostname', ...(showSite ? ['Site'] : []), 'Type', 'Status', 'Billing', 'Last user', 'Patching', 'Antivirus', 'Alerts', ''];

  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="truncate text-[18px] font-[650] tracking-[-0.015em] text-foreground">{title}</h2>
          <div className="mt-0.5 text-sm text-muted-foreground">
            {subtitle}
          </div>
          {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
        </div>
        {portalUrl && (
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <a href={portalUrl} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Open in Datto RMM</a>
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 min-[1400px]:grid-cols-7">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-md border bg-background/40 px-3 py-2">
            <div title={t.label} className="truncate text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{t.label}</div>
            <div className={cn(
              'mt-0.5 text-[18px] font-[650] tabular-nums',
              t.value > 0 && t.tone === 'warning' && 'text-warning',
              t.value > 0 && t.tone === 'destructive' && 'text-destructive',
            )}>{t.value}</div>
          </div>
        ))}
      </div>

      {devices.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No devices.</p>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-3">
            <SearchInput withIcon placeholder={showSite ? 'Hostname, site, user, IP, serial…' : 'Hostname, user, IP, serial…'} className="w-72" value={q} onValueChange={setQ} />
            <label className="flex items-center gap-2 text-xs text-muted-foreground" title="Datto RMM agents not billed on a live Autotask contract service line">
              <input type="checkbox" checked={notBilledOnly} onChange={(e) => setNotBilledOnly(e.target.checked)} className="h-3.5 w-3.5" />
              Not billed only
            </label>
            {billing.error ? <span className="text-xs text-destructive">Autotask billing unavailable</span> : null}
            <span className="text-xs text-muted-foreground">{shown.length} of {devices.length}</span>
          </div>
          <div className="mt-3 overflow-x-auto rounded-md border">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 200 }} />
                {showSite && <col style={{ width: 110 }} />}
                <col style={{ width: 140 }} /><col style={{ width: 135 }} /><col style={{ width: 140 }} /><col style={{ width: 160 }} />
                <col style={{ width: 140 }} /><col style={{ width: 120 }} /><col style={{ width: 75 }} /><col style={{ width: 64 }} />
              </colgroup>
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>{headers.map((h, i) => <th key={`${h}-${i}`} className="px-3 py-2 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y">
                {shown.length === 0 && (
                  <tr><td colSpan={headers.length} className="px-3 py-8 text-center text-muted-foreground">No devices match.</td></tr>
                )}
                {shown.map((d) => (
                  <tr key={d.uid} className="hover:bg-muted/30">
                    <td className="overflow-hidden px-3 py-2">
                      <Link to={rmmDevicePath(d.uid)} className="block truncate font-medium text-foreground hover:underline" title={d.description ?? d.hostname}>
                        {d.hostname}
                      </Link>
                    </td>
                    {showSite && (
                      <td className="overflow-hidden px-3 py-2">
                        <Link to={rmmSitePath(d.siteUid)} className="block truncate font-mono text-xs hover:underline">{d.siteName}</Link>
                      </td>
                    )}
                    <td className="truncate px-3 py-2" title={d.type ?? undefined}>{d.category ?? '—'}</td>
                    <td className="px-3 py-2"><DeviceStatusBadge d={d} /></td>
                    <td className="px-3 py-2">{billing.isLoading ? <span className="text-xs text-muted-foreground">…</span> : <BillingBadge b={billOf(d)} />}</td>
                    <td className="truncate px-3 py-2" title={d.lastLoggedInUser ?? undefined}>
                      {d.lastLoggedInUser ? d.lastLoggedInUser.slice(d.lastLoggedInUser.lastIndexOf('\\') + 1) : '—'}
                    </td>
                    <td className="px-3 py-2"><PatchBadge d={d} /></td>
                    <td className="px-3 py-2"><AvBadge d={d} /></td>
                    <td className="px-3 py-2"><AlertCountBadge d={d} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-0.5">
                        {d.webRemoteUrl && (
                          <a href={d.webRemoteUrl} target="_blank" rel="noreferrer" title="Web Remote"
                            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                            <MonitorPlay className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <a href={d.portalUrl} target="_blank" rel="noreferrer" title="Open in Datto RMM"
                          className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
